import axios from "@/plugins/axios";
import { collectQuestions, normalizeComposition } from "@/components/Client/CanonicalQuestionnaire/composition";
import { collectVisibleQuestions, filterVisibleComposition } from "@/components/Client/CanonicalQuestionnaire/visibility";
import {
  flattenVisibleScreens,
  nextScreen as nextVisibleScreen,
  overallProgressPercent,
  prevScreen as prevVisibleScreen,
  stageProgressPercent,
} from "@/components/Client/CanonicalQuestionnaire/navigation";

const SAVE_DEBOUNCE_MS = 500;

function answerKeys(question, value) {
  const entries = {};
  if (question.canonicalQuestionId) entries[question.canonicalQuestionId] = value;
  if (question.machineKey) entries[question.machineKey] = value;
  return entries;
}

function answerMapsFromComposition(raw) {
  const model = normalizeComposition(raw);
  const persisted = {};
  const prefill = {};
  for (const question of collectQuestions(model)) {
    if (question.hasPersistedAnswer) {
      Object.assign(persisted, answerKeys(question, question.persistedValue));
    }
    if (question.prefillValue !== undefined) {
      Object.assign(prefill, answerKeys(question, question.prefillValue));
    }
  }
  return { model, persisted, prefill };
}

function isClearValue(value, payload = {}) {
  if (payload.clear === true) return true;
  return value === null || value === undefined;
}

function requeueAnswers(commit, answers) {
  for (const item of answers) {
    commit("queuePendingSave", {
      canonicalQuestionId: item.canonical_question_id,
      value: item.value,
      clear: item.clear === true,
    });
  }
}

export default {
  namespaced: true,

  state: () => ({
    composition: null,
    persistedAnswers: {},
    prefilledAnswers: {},
    liveAnswers: {},
    loading: false,
    saving: false,
    loadError: null,
    saveError: null,
    organizationId: null,
    submissionId: null,
    currentScreenKey: null,
    _saveTimer: null,
    _pendingSaves: {},
    _flushGeneration: 0,
  }),

  getters: {
    answersForVisibility(state) {
      return { ...state.prefilledAnswers, ...state.persistedAnswers, ...state.liveAnswers };
    },

    visibleQuestions(state, getters) {
      if (!state.composition) return [];
      return collectVisibleQuestions(state.composition, getters.answersForVisibility);
    },

    visibleScreens(state, getters) {
      if (!state.composition) return [];
      return flattenVisibleScreens(state.composition, getters.answersForVisibility);
    },

    stages(state) {
      return state.composition?.stages || [];
    },

    overallProgress(state, getters) {
      if (!state.composition) return 0;
      return overallProgressPercent(state.composition, getters.answersForVisibility);
    },

    stageProgress: (state, getters) => (stageKeyOrId) => {
      if (!state.composition) return 0;
      const tree = filterVisibleComposition(state.composition, getters.answersForVisibility);
      const stage = (tree.stages || []).find(
        (s) =>
          s.entityId === stageKeyOrId ||
          s.machineKey === stageKeyOrId ||
          s.key === stageKeyOrId
      );
      if (!stage) return 0;
      return stageProgressPercent(stage, getters.answersForVisibility);
    },

    currentScreenEntry(state, getters) {
      if (!state.currentScreenKey) {
        return getters.visibleScreens[0] || null;
      }
      return (
        getters.visibleScreens.find(
          ({ screen }) =>
            screen.entityId === state.currentScreenKey ||
            screen.machineKey === state.currentScreenKey ||
            screen.key === state.currentScreenKey
        ) || getters.visibleScreens[0] || null
      );
    },
  },

  mutations: {
    setLoading(state, value) {
      state.loading = Boolean(value);
    },
    setSaving(state, value) {
      state.saving = Boolean(value);
    },
    setLoadError(state, message) {
      state.loadError = message || null;
    },
    setSaveError(state, message) {
      state.saveError = message || null;
    },
    setContext(state, { organizationId, submissionId }) {
      state.organizationId = organizationId || null;
      state.submissionId = submissionId || null;
    },
    setComposition(state, raw) {
      state.composition = raw ? normalizeComposition(raw) : null;
    },
    setPersistedAnswers(state, answers) {
      state.persistedAnswers = answers && typeof answers === "object" ? { ...answers } : {};
    },
    mergePersistedAnswers(state, answers) {
      state.persistedAnswers = {
        ...state.persistedAnswers,
        ...(answers && typeof answers === "object" ? answers : {}),
      };
    },
    setPrefilledAnswers(state, answers) {
      state.prefilledAnswers = answers && typeof answers === "object" ? { ...answers } : {};
    },
    setLiveAnswer(state, { canonicalQuestionId, machineKey, value }) {
      const next = { ...state.liveAnswers };
      if (canonicalQuestionId) next[canonicalQuestionId] = value;
      if (machineKey) next[machineKey] = value;
      state.liveAnswers = next;
    },
    clearLiveAnswers(state) {
      state.liveAnswers = {};
    },
    setCurrentScreenKey(state, key) {
      state.currentScreenKey = key || null;
    },
    queuePendingSave(state, { canonicalQuestionId, value, clear }) {
      if (!canonicalQuestionId) return;
      state._pendingSaves = {
        ...state._pendingSaves,
        [canonicalQuestionId]: clear
          ? { canonical_question_id: canonicalQuestionId, clear: true }
          : { canonical_question_id: canonicalQuestionId, value },
      };
    },
    clearPendingSaves(state) {
      state._pendingSaves = {};
    },
    setSaveTimer(state, timer) {
      state._saveTimer = timer;
    },
    bumpFlushGeneration(state) {
      state._flushGeneration += 1;
    },
    reset(state) {
      if (state._saveTimer) clearTimeout(state._saveTimer);
      state.composition = null;
      state.persistedAnswers = {};
      state.prefilledAnswers = {};
      state.liveAnswers = {};
      state.loading = false;
      state.saving = false;
      state.loadError = null;
      state.saveError = null;
      state.organizationId = null;
      state.submissionId = null;
      state.currentScreenKey = null;
      state._saveTimer = null;
      state._pendingSaves = {};
      state._flushGeneration = 0;
    },
  },

  actions: {
    async loadComposition({ commit, state }, { organizationId, submissionId } = {}) {
      if (!organizationId) {
        commit("setLoadError", "organization_id is required");
        return null;
      }
      commit("setLoading", true);
      commit("setLoadError", null);
      commit("setContext", { organizationId, submissionId: submissionId || null });
      try {
        const params = { organization_id: organizationId };
        if (submissionId) params.submission_id = submissionId;
        const { data } = await axios.get("/application-builder/questionnaire", {
          params,
        });
        if (!data?.success) {
          commit("setLoadError", data?.message || "Failed to load questionnaire");
          commit("setComposition", null);
          return null;
        }
        const questionnaire = data.questionnaire || null;
        const { persisted, prefill } = answerMapsFromComposition(questionnaire);
        commit("setComposition", questionnaire);
        commit("setPersistedAnswers", persisted);
        commit("setPrefilledAnswers", prefill);
        commit("clearLiveAnswers");

        const firstScreen = flattenVisibleScreens(state.composition, {
          ...prefill,
          ...persisted,
        })[0];
        commit(
          "setCurrentScreenKey",
          firstScreen ? firstScreen.screen.machineKey || firstScreen.screen.entityId : null
        );
        return questionnaire;
      } catch (error) {
        commit(
          "setLoadError",
          error?.response?.data?.message || error.message || "Failed to load questionnaire"
        );
        commit("setComposition", null);
        return null;
      } finally {
        commit("setLoading", false);
      }
    },

    async refreshComposition({ commit, state }) {
      if (!state.organizationId) return null;
      const params = { organization_id: state.organizationId };
      if (state.submissionId) params.submission_id = state.submissionId;
      const { data } = await axios.get("/application-builder/questionnaire", {
        params,
        withoutLoading: true,
      });
      if (!data?.success) {
        commit("setLoadError", data?.message || "Failed to refresh questionnaire");
        return null;
      }
      const questionnaire = data.questionnaire || null;
      const previousIds = new Set(
        (state.composition ? collectQuestions(state.composition) : [])
          .map((q) => q.canonicalQuestionId)
          .filter(Boolean)
      );
      const { persisted, prefill } = answerMapsFromComposition(questionnaire);
      commit("setComposition", questionnaire);
      commit("setPrefilledAnswers", prefill);
      commit("mergePersistedAnswers", persisted);

      const nextIds = new Set(
        collectQuestions(state.composition)
          .map((q) => q.canonicalQuestionId)
          .filter(Boolean)
      );
      const dropped = [...previousIds].filter((id) => !nextIds.has(id));
      if (dropped.length) {
        commit(
          "setLoadError",
          `Composition shrank unexpectedly (dropped ${dropped.length} question(s))`
        );
      }
      return { questionnaire, dropped };
    },

    onAnswerChange({ commit, dispatch, state }, payload = {}) {
      const canonicalQuestionId = payload.canonicalQuestionId || null;
      const machineKey = payload.machineKey || null;
      const value = payload.value;
      const clear = isClearValue(value, payload);

      commit("setLiveAnswer", {
        canonicalQuestionId,
        machineKey,
        value: clear ? null : value,
      });
      commit("queuePendingSave", {
        canonicalQuestionId,
        value,
        clear,
      });

      if (state._saveTimer) clearTimeout(state._saveTimer);
      const timer = setTimeout(() => {
        dispatch("flushSaves");
      }, SAVE_DEBOUNCE_MS);
      commit("setSaveTimer", timer);
    },

    async flushSaves({ commit, state, dispatch }) {
      if (state._saveTimer) {
        clearTimeout(state._saveTimer);
        commit("setSaveTimer", null);
      }
      const answers = Object.values({ ...state._pendingSaves });
      if (!answers.length) return true;
      if (!state.organizationId) {
        commit("setSaveError", "organization_id is required to save answers");
        return false;
      }

      commit("bumpFlushGeneration");
      const generation = state._flushGeneration;
      commit("setSaving", true);
      commit("setSaveError", null);
      commit("clearPendingSaves");
      try {
        const body = {
          organization_id: state.organizationId,
          answers,
        };
        if (state.submissionId) body.submission_id = state.submissionId;

        const { data } = await axios.post("/application-builder/answers", body, {
          withoutLoading: true,
        });
        if (!data?.success) {
          commit("setSaveError", data?.message || "Failed to save answers");
          requeueAnswers(commit, answers);
          return false;
        }

        const promoted = { ...state.persistedAnswers };
        const questions = state.composition ? collectQuestions(state.composition) : [];
        for (const item of answers) {
          const id = item.canonical_question_id;
          const nextValue = item.clear ? null : item.value;
          promoted[id] = nextValue;
          const match = questions.find((q) => q.canonicalQuestionId === id);
          if (match?.machineKey) promoted[match.machineKey] = nextValue;
        }
        commit("setPersistedAnswers", promoted);

        if (
          state._flushGeneration === generation &&
          Object.keys(state._pendingSaves).length
        ) {
          await dispatch("flushSaves");
        }
        return true;
      } catch (error) {
        commit(
          "setSaveError",
          error?.response?.data?.message || error.message || "Failed to save answers"
        );
        requeueAnswers(commit, answers);
        return false;
      } finally {
        commit("setSaving", false);
      }
    },

    goToScreen({ commit }, screenKey) {
      commit("setCurrentScreenKey", screenKey);
    },

    goNext({ commit, state, getters }) {
      const nxt = nextVisibleScreen(
        state.composition,
        getters.answersForVisibility,
        state.currentScreenKey
      );
      if (!nxt) return null;
      const key = nxt.screen.machineKey || nxt.screen.entityId;
      commit("setCurrentScreenKey", key);
      return nxt;
    },

    goPrev({ commit, state, getters }) {
      const prev = prevVisibleScreen(
        state.composition,
        getters.answersForVisibility,
        state.currentScreenKey
      );
      if (!prev) return null;
      const key = prev.screen.machineKey || prev.screen.entityId;
      commit("setCurrentScreenKey", key);
      return prev;
    },
  },
};
